import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Shield, CheckCircle2, KeyRound, Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { logAuthEvent } from "@/lib/auditLog";

const signupSchema = z.object({
  inviteCode: z.string().min(1, "Invite code is required"),
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function Signup() {
  const [inviteCode, setInviteCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = signupSchema.safeParse({ inviteCode, email, password, confirmPassword });
    if (!result.success) {
      toast({ title: "Validation Error", description: result.error.errors[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);

    try {
      // Step 1: Validate the invite code server-side
      const validateRes = await supabase.functions.invoke("validate-invite-code", {
        body: { code: inviteCode.trim() },
      });

      if (validateRes.error || !validateRes.data?.valid) {
        toast({
          title: "Invalid invite code",
          description: validateRes.data?.error || "Please check your invite code and try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const codeId = validateRes.data.code_id;

      // Step 2: Create the account
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        let description = error.message;
        if (error.message.toLowerCase().includes("hibp") || error.message.toLowerCase().includes("leaked") || error.message.toLowerCase().includes("pwned") || error.message.toLowerCase().includes("breach")) {
          description = "This password has appeared in a known data breach and cannot be used. Please choose a different, more unique password.";
        } else if (error.message.toLowerCase().includes("too short") || error.message.toLowerCase().includes("at least")) {
          description = "Password must be at least 8 characters long.";
        }
        toast({ title: "Password not accepted", description, variant: "destructive" });
        setLoading(false);
        return;
      }

      // Step 3: Claim the invite code (mark as used)
      const userId = data.user?.id;
      if (userId) {
        await supabase.functions.invoke("claim-invite-code", {
          body: { code_id: codeId, user_id: userId },
        });
      }

      logAuthEvent("signup", { email });
      if (data.session) {
        navigate("/dashboard");
      } else {
        setSent(true);
      }
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary/20">
              <CheckCircle2 className="h-6 w-6 text-secondary" />
            </div>
            <CardTitle className="font-display text-2xl">Check your email</CardTitle>
            <CardDescription>We've sent a confirmation link to <strong>{email}</strong>. Click it to activate your account.</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/login" className="text-primary hover:underline text-sm">Back to login</Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const trialBenefits = [
    "Full access during beta — no credit card needed",
    "Track 29+ credentials automatically",
    "Renewal reminders at 90, 60, 30 days",
    "Export your data anytime",
  ];

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);

  const pwRequirements = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Upper & lowercase letters", met: hasUpper && hasLower },
    { label: "At least one number", met: hasNumber },
    { label: "Not a commonly breached password", met: password.length >= 8 && hasUpper && hasLower && hasNumber },
    { label: "Passwords match", met: password.length > 0 && confirmPassword.length > 0 && password === confirmPassword },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="font-display text-2xl">Join the Beta</CardTitle>
          <CardDescription>Enter your invite code to create an account.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSignup}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-code" className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" /> Invite Code
              </Label>
              <Input
                id="invite-code"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter your code"
                className="font-mono tracking-wider"
                maxLength={20}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" required className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password</Label>
              <div className="relative">
                <Input id="confirm" type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required className="pr-10" />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {password.length > 0 && (
              <ul className="space-y-1">
                {pwRequirements.map((r) => (
                  <li key={r.label} className={`flex items-center gap-2 text-xs ${r.met ? "text-secondary" : "text-muted-foreground"}`}>
                    <CheckCircle2 className={`h-3.5 w-3.5 ${r.met ? "text-secondary" : "text-muted-foreground/40"}`} />
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
            <ul className="space-y-2 pt-2">
              {trialBenefits.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-secondary flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Verifying code…" : "Join Beta"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </p>
            <p className="text-xs text-muted-foreground text-center">
              By signing up you agree to our{" "}
              <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
